import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Video, Play, Download, Send, User, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import DistributeVideoDialog from './DistributeVideoDialog';

interface RecordingsViewProps {
  companyId: string;
}

interface Recording {
  id: string;
  worker_id: string;
  completed_at: string;
  response_data: {
    // Legacy audio fields
    recording_url?: string;
    audioUrl?: string;
    duration_seconds?: number;
    recordingDuration?: number;
    // New video fields
    videoUrl?: string;
    transcription?: string;
  };
  worker_profiles: {
    display_name: string;
    team: string;
  };
}

export default function RecordingsView({ companyId }: RecordingsViewProps) {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  
  // Distribute dialog state
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState<Recording | null>(null);

  useEffect(() => {
    loadRecordings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Get video/audio URL from recording (prefer video over audio)
  const getRecordingUrl = (recording: Recording) =>
    recording.response_data?.videoUrl || 
    recording.response_data?.recording_url || 
    recording.response_data?.audioUrl;

  const isVideoRecording = (recording: Recording) =>
    Boolean(recording.response_data?.videoUrl);

  const getDurationSeconds = (recording: Recording) =>
    recording.response_data?.duration_seconds ?? recording.response_data?.recordingDuration ?? 0;

  const loadRecordings = async () => {
    try {
      // @ts-ignore - Avoid deep type instantiation error
      const templatesResponse = await supabase
        .from('quest_templates')
        .select('id')
        .eq('company_id', companyId)
        .eq('intervention_type', 'record_safety_message');

      if (!templatesResponse.data?.length) {
        setRecordings([]);
        setLoading(false);
        return;
      }

      const templateIds = templatesResponse.data.map((t: any) => t.id);

      // @ts-ignore - Avoid deep type instantiation error
      const { data, error } = await supabase
        .from('worker_quests')
        .select(`
          id,
          worker_id,
          completed_at,
          response_data,
          worker_profiles!inner(display_name, team, company_id)
        `)
        .in('template_id', templateIds)
        .eq('status', 'completed')
        .eq('worker_profiles.company_id', companyId)
        .not('response_data', 'is', null)
        .order('completed_at', { ascending: false });

      if (error) throw error;

      // Filter recordings that have video or audio URL
      const validRecordings = (data || []).filter((r: any) =>
        Boolean(r.response_data?.videoUrl || r.response_data?.recording_url || r.response_data?.audioUrl)
      ) as unknown as Recording[];

      setRecordings(validRecordings);
    } catch (error) {
      console.error('Error loading recordings:', error);
      toast.error('Failed to load recordings');
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (recording: Recording) => {
    const url = getRecordingUrl(recording);
    if (!url) {
      toast.error('Recording URL not found');
      return;
    }

    if (playingId === recording.id) {
      if (videoRef.current) {
        videoRef.current.pause();
      }
      setPlayingId(null);
      return;
    }

    setPlayingId(recording.id);
  };

  const handleDownload = async (recording: Recording) => {
    const url = getRecordingUrl(recording);
    if (!url) {
      toast.error('Recording URL not found');
      return;
    }

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      const ext = isVideoRecording(recording) ? 'webm' : 'webm';
      a.download = `recording_${recording.worker_profiles.display_name.replace(/\s+/g, '_')}_${new Date(recording.completed_at).toISOString().split('T')[0]}.${ext}`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download recording');
    }
  };

  const handleDistribute = (recording: Recording) => {
    setSelectedRecording(recording);
    setDistributeOpen(true);
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Video className="h-5 w-5 text-destructive" />
            <div>
              <p className="text-2xl font-bold">{recordings.length}</p>
              <p className="text-sm text-muted-foreground">Total Recordings</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-bold">
                {formatDuration(recordings.reduce((acc, r) => acc + getDurationSeconds(r), 0))}
              </p>
              <p className="text-sm text-muted-foreground">Total Duration</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Recordings Grid */}
      {recordings.length === 0 ? (
        <Card className="p-8 text-center">
          <Video className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No video recordings yet</p>
          <p className="text-sm text-muted-foreground mt-2">
            Recordings will appear here when workers complete safety video quests
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {recordings.map((recording, index) => (
            <motion.div
              key={recording.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="overflow-hidden">
                {/* Video Preview / Player */}
                <div className="relative aspect-video bg-black">
                  {playingId === recording.id ? (
                    <video
                      ref={videoRef}
                      src={getRecordingUrl(recording)}
                      controls
                      autoPlay
                      className="w-full h-full object-contain"
                      onEnded={() => setPlayingId(null)}
                    />
                  ) : (
                    <div 
                      className="relative w-full h-full bg-muted cursor-pointer group"
                      onClick={() => handlePlay(recording)}
                    >
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                          <Play className="h-7 w-7 text-primary ml-1" />
                        </div>
                      </div>
                      {/* Duration badge */}
                      <div className="absolute bottom-2 right-2">
                        <Badge variant="secondary" className="bg-black/70 text-white text-xs">
                          {formatDuration(getDurationSeconds(recording))}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>

                {/* Info & Actions */}
                <div className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="font-semibold truncate">{recording.worker_profiles.display_name}</span>
                    <Badge variant="outline" className="text-xs flex-shrink-0">
                      {recording.worker_profiles.team}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {new Date(recording.completed_at).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>

                  {/* Actions */}
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" variant="outline" onClick={() => handleDownload(recording)} className="flex-1">
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="default" 
                      onClick={() => handleDistribute(recording)}
                      className="flex-1 gap-1"
                    >
                      <Send className="h-4 w-4" />
                      Distribute
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Distribute Dialog */}
      {selectedRecording && (
        <DistributeVideoDialog
          open={distributeOpen}
          onOpenChange={setDistributeOpen}
          videoUrl={getRecordingUrl(selectedRecording) || ''}
          videoTitle="Safety Video Message"
          workerName={selectedRecording.worker_profiles.display_name}
          companyId={companyId}
        />
      )}
    </div>
  );
}
